import React, { useEffect, useState } from 'react';

import moment from 'moment';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import { UiAutocomplete } from '../ui/ui-autocomplete';
import { UiButton } from '../ui/ui-button';
import { UiDialog } from '../ui/ui-dialog';

import { DataComparisonDateRow } from './components/data-comparison-date-row';
import { DataComparisonRow } from './components/data-comparison-row';

import { employeeName } from '../employees/utils/employee-name';
import { formatPhoneNumber } from '../util/formatters';
import { SORT_ORDER, sortAlphabetically } from '../util/sort-functions';
import { TableHeaderCell } from '../widgets/table-components/table-header-cell';

type InputProps = {
  employeeToKeep: Employee;
  employeeToRemove?: Employee;
  employees: Array<Employee>;
  mergeResolution: Record<keyof EmployeeMergeResolutionType, string>;

  setEmployeeIdToKeep: (employeeId: number | null) => void;
  setMergeResolution: (resolutions: Record<keyof EmployeeMergeResolutionType, string>) => void;

  nextStep: () => void;
  close: () => void;
};

export const EmployeeMergeResolver = (props: InputProps) => {
  const {
    employeeToKeep,
    employeeToRemove,
    employees,
    mergeResolution,
    setEmployeeIdToKeep,
    setMergeResolution,
    nextStep,
    close,
  } = props;
  const [fieldsWithDiscrepancies, setFieldsWithDiscrepancies] = useState<Array<string>>([]);
  const [discrepanciesResolved, setDiscrepanciesResolved] = useState<boolean>(false);

  const employeeOptions = employees
    .map((employee) => ({
      value: employee.employeeId.toString(),
      label: employeeName(employee),
    }))
    .sort(sortAlphabetically((empOption) => empOption.label, SORT_ORDER.ASC));

  const handleSelectEmployee = (newValue: string) => {
    // Reset discrepancies and resolutions if we change the employee to merge
    setFieldsWithDiscrepancies([]);
    setMergeResolution({} as Record<keyof EmployeeMergeResolutionType, string>);
    setEmployeeIdToKeep(parseInt(newValue, 10));
  };

  const updateField =
    (field: keyof EmployeeMergeResolutionType) => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.value) {
        setMergeResolution({
          ...mergeResolution,
          [field]: e.target.value,
        });
        setFieldsWithDiscrepancies(
          fieldsWithDiscrepancies.filter((fieldName) => fieldName !== field),
        );
      }
    };

  const addDiscrepancy = (field: keyof EmployeeMergeResolutionType) => {
    fieldsWithDiscrepancies.push(field);
    setFieldsWithDiscrepancies(fieldsWithDiscrepancies);
  };

  useEffect(() => {
    setDiscrepanciesResolved(fieldsWithDiscrepancies.length === 0);
  }, [employeeToRemove, fieldsWithDiscrepancies]);

  const firstFormattedDateOrNull = (
    resolutionDate: string | null,
    employeeToKeepDate: string | null,
    employeeToRemoveDate: string | null,
  ) => {
    // resolutionDate is already a string formatted correctly (if it exists), so we can just return without re-parsing
    if (resolutionDate) {
      return resolutionDate;
    }
    const dateString = employeeToKeepDate || employeeToRemoveDate;
    return dateString ? moment.utc(dateString).format('MM-DD-YYYY') : null;
  };

  const handleNextStep = () => {
    // If we have a merge resolution value, use that, otherwise fall back to the employeeToKeep then employeeToRemove value
    // Filters out null, undefined, and '' values, so we don't clear information on merge
    const allResolutions = Object.fromEntries(
      Object.entries({
        startDate: firstFormattedDateOrNull(
          mergeResolution.startDate,
          employeeToKeep.startDate,
          employeeToRemove?.startDate,
        ),
        nickname: mergeResolution.nickname || employeeToKeep.nickname || employeeToRemove?.nickname,
        jobTitle: mergeResolution.jobTitle || employeeToKeep.jobTitle || employeeToRemove?.jobTitle,
        firstName:
          mergeResolution.firstName ||
          employeeToKeep.Contact.Person.firstName ||
          employeeToRemove?.Contact?.Person?.firstName,
        lastName:
          mergeResolution.lastName ||
          employeeToKeep.Contact.Person.lastName ||
          employeeToRemove?.Contact?.Person?.lastName,
        email:
          mergeResolution.email ||
          employeeToKeep.Contact.Person.email ||
          employeeToRemove?.Contact?.Person?.email,
        phoneNumber:
          mergeResolution.phoneNumber ||
          formatPhoneNumber(employeeToKeep.Contact.Person.phoneNumber) ||
          formatPhoneNumber(employeeToRemove?.Contact?.Person?.phoneNumber),
        birthdate: firstFormattedDateOrNull(
          mergeResolution.birthdate,
          employeeToKeep.Contact.Person.birthdate,
          employeeToRemove?.Contact?.Person?.birthdate,
        ),
      }).filter(([, resolutionValue]) => Boolean(resolutionValue)),
    ) as Record<keyof EmployeeMergeResolutionType, string>;
    setMergeResolution(allResolutions);
    nextStep();
  };

  return (
    <>
      <UiDialog.Content>
        <div>
          Select another employee to combine with {employeeName(employeeToKeep)}. This will merge
          all associated data into {employeeName(employeeToKeep)}&apos;s profile.
        </div>
        <UiAutocomplete
          className="u-margin"
          label="Select employee to combine"
          options={employeeOptions}
          value={
            employeeOptions.find((o) => parseInt(o.value, 10) === employeeToRemove?.employeeId)
              ?.value || ''
          }
          onChange={handleSelectEmployee}
        />
        {!discrepanciesResolved ? (
          <div className="u-margin typo-label">
            There are data conflicts between the selected employee records. Please select a value to
            keep for each highlighted row below.
          </div>
        ) : null}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableHeaderCell />
              <TableHeaderCell>{employeeName(employeeToKeep)}</TableHeaderCell>
              <TableHeaderCell>Selected Employee</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <DataComparisonRow
              label="First Name"
              field="firstName"
              existingDataValue={employeeToKeep.Contact.Person.firstName}
              comparisonDataValue={employeeToRemove?.Contact?.Person?.firstName}
              selectedValue={mergeResolution.firstName}
              updateField={updateField}
              addDiscrepancy={addDiscrepancy}
            />
            <DataComparisonRow
              label="Last Name"
              field="lastName"
              existingDataValue={employeeToKeep.Contact.Person.lastName}
              comparisonDataValue={employeeToRemove?.Contact?.Person?.lastName}
              selectedValue={mergeResolution.lastName}
              updateField={updateField}
              addDiscrepancy={addDiscrepancy}
            />
            <DataComparisonRow
              label="Nickname"
              field="nickname"
              existingDataValue={employeeToKeep.nickname}
              comparisonDataValue={employeeToRemove?.nickname}
              selectedValue={mergeResolution.nickname}
              updateField={updateField}
              addDiscrepancy={addDiscrepancy}
            />
            <DataComparisonRow
              label="Email"
              field="email"
              existingDataValue={employeeToKeep.Contact.Person.email}
              comparisonDataValue={employeeToRemove?.Contact?.Person?.email}
              selectedValue={mergeResolution.email}
              updateField={updateField}
              addDiscrepancy={addDiscrepancy}
            />
            <DataComparisonRow
              label="Phone Number"
              field="phoneNumber"
              existingDataValue={formatPhoneNumber(employeeToKeep.Contact.Person.phoneNumber)}
              comparisonDataValue={formatPhoneNumber(
                employeeToRemove?.Contact?.Person?.phoneNumber,
              )}
              selectedValue={formatPhoneNumber(mergeResolution.phoneNumber)}
              updateField={updateField}
              addDiscrepancy={addDiscrepancy}
            />
            <DataComparisonDateRow
              label="Start Date"
              field="startDate"
              existingDataValue={employeeToKeep.startDate}
              comparisonDataValue={employeeToRemove?.startDate}
              selectedValue={mergeResolution.startDate}
              updateField={updateField}
              addDiscrepancy={addDiscrepancy}
            />
            <DataComparisonRow
              label="Job Title"
              field="jobTitle"
              existingDataValue={employeeToKeep.jobTitle || ''}
              comparisonDataValue={employeeToRemove?.jobTitle}
              selectedValue={mergeResolution.jobTitle}
              updateField={updateField}
              addDiscrepancy={addDiscrepancy}
            />
            <DataComparisonDateRow
              label="Birthdate"
              field="birthdate"
              existingDataValue={employeeToKeep.Contact.Person.birthdate}
              comparisonDataValue={employeeToRemove?.Contact?.Person?.birthdate}
              selectedValue={mergeResolution.birthdate}
              updateField={updateField}
              addDiscrepancy={addDiscrepancy}
            />
          </TableBody>
        </Table>
      </UiDialog.Content>
      <UiDialog.Actions>
        <UiButton variant="dismiss" onClick={close}>
          Cancel
        </UiButton>
        <div>
          <UiButton
            variant="primary"
            className="u-margin-left"
            disabled={!employeeToRemove || !discrepanciesResolved}
            onClick={handleNextStep}
          >
            Next
          </UiButton>
        </div>
      </UiDialog.Actions>
    </>
  );
};
